/**
 * KED-1695 Tax return field type wrapper
 * https://kpmg-au-ddx.atlassian.net/browse/KED-1695
 */
import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import {
  Answer,
  Question,
  TaxReturnService
} from "../../../../swaggerServices";
import {
  UntypedFormGroup,
  AbstractControl,
  Validators,
  ValidatorFn,
  UntypedFormControl
} from "@angular/forms";
import { head } from "lodash-es";
import { makeTextFieldFormControl } from "../text-field/text-field.component";
import { makeImageFieldFormControl } from "../image-field/image-field.component";
import { makeNumberFieldFormControl } from "../number-field/number-field.component";
import { makeCheckboxFieldFormControl } from "../checkbox-field/checkbox-field.component";
import { makeRadioFieldFormControl } from "../radio-field/radio-field.component";
import { makeDateFieldFormControl } from "../date-field/date-field.component";
import { makeDropdownFieldFormControl } from "../dropdown-field/dropdown-field.component";
import { makeFileFieldFormControl } from "../file-field/file-field.component";
import {
  IAnswerFlag,
  IGroup,
  ISubGroup
} from "../../../app/tax-returns/tax-return/subcategory/subcategory";
import { Subject } from "rxjs";
import { ClientAnswer } from "../../../../swaggerServices";
import { AnswerWarning } from "../../../../swaggerServices/model/answerWarning";
import { IComment } from "../../../app/tax-returns/tax-return/subcategory/comments/comments.component";
import { ConfigStateService } from "../../../app/config/config-state.service";
import { Tenant } from "../../types/tenant.enum";
import { BaseURLService } from "../../util/baseUrl.service";
import { UserInfoService } from "../../../app/user-info.service";
import { CommentValue } from "../../../app/tax-returns/tax-return/subcategory/comments/attachments/attachments.component";

interface IWarning {
  message?: string;
  warningType?: string;
}

interface IClientAnswer extends ClientAnswer {
  isGlobal?: boolean;
}

export interface IAnswerWarning extends AnswerWarning {
  warning?: IWarning;
  question?: string;
  questionId?: number;
  createdAtString?: string;
  answer?: IAnswer;
  replyTo?: boolean;
  replyComment?: CommentValue;
  replyToClient?: boolean;
  replyClient?: string;
  replies?: IComment[];
  rowIndex?: number;
  externalUrl?: string;
  isCollapsed?: boolean;
}

export interface IAnswer extends Answer, IClientAnswer {
  answerWarnings?: IAnswerWarning[];
  comments?: IComment[];
  answers?: IAnswer[];
  flags?: IAnswerFlag[];
  answerFlags?: IAnswerFlag[];
  isNew?: boolean; // for table row answers to highlight cell when adding new row
  isActive?: boolean; // for table rows to highlight cell when selecting cell
  _rowIndex?: number;
  overrideStatus?: number;
}

export interface IFormField extends Question {
  topLevel?: boolean; // top level questions are displayed on main tax return screen, if false it is displayed on drilldown view
  categoryId?: number;
  subCategoryId?: number;
  subCategoryOrder?: number; // order of question at the main tax form screen
  subGroupOrder?: {
    order?: number;
  };
  hint?: string;
  default?: any;
  placeholder?: string;
  dependantMandatoryParentValue?: any;
  displayFieldType?: any;
  minLength?: number;
  isCurrency?: boolean;
  isPercentage?: boolean;
  minDate?: string;
  maxDate?: string;
  questionDropdowns?: ListOption[];
  dropdownDirection?: "auto" | "upward" | "downward";
  answers?: IAnswer[];
  validators?: ValidatorFn[];
  isTab?: boolean;
  hintTxt?: string; // long hint text
  isGlobal?: boolean; // whether field is applied to all population rules
  APSRef?: string;
  APSSection?: string;
  APSSubsection?: string;
  APSQuestionGrouping?: string;
  APSDimension?: number;
  isAnswerGroup?: boolean;
  createdAt?: string;
  updatedAt?: string;
  tableRow?: number; // used in edit fields to specify table row
  isRowId?: boolean;
  taxReturnTypeId?: number;
  toggleOff?: boolean;
  lockType?: string;
}

export interface ListOption {
  id: number;
  value: string;
  display?: string;
  order?: number;
  selected?: boolean;
  disabled?: boolean;
  edit?: boolean;
  status?: number;
}

@Component({
    selector: "app-form-field-wrapper",
    templateUrl: "./form-field-wrapper.component.html",
    styleUrls: ["./form-field-wrapper.component.scss"],
    host: {
        "[class.visibly-disabled]": "visiblyDisabled"
    },
    standalone: false
})
export class FormFieldWrapperComponent implements OnInit {
  @Input() isSmartForm?: boolean = false;
  @Input() formGroup: UntypedFormGroup; // form group to pass into child inputs to init validators
  @Input() questions: Array<IFormField>;
  @Input() question: IFormField;
  @Input() labelClick?: Function;
  @Input() labelTitle?: string;
  @Input() groupId?: number;
  @Input() subGroup?: ISubGroup;
  @Input() setGlobal?: Function;
  @Input() disabled?: boolean;
  @Input() visiblyDisabled?: boolean;
  @Input() row?: number;
  @Input() hasNotification?: Function;
  @Input() autofocus = false;
  @Input() showHiddenQuestion = false;
  @Input() hideClearOverRideQuestionIcon: boolean;
  @Input() inputQuestionId?: number;
  @Input() isUnassignedPopulation: boolean;
  @Output() blur: EventEmitter<any> = new EventEmitter();
  @Output() refreshTaxReturn: EventEmitter<any> = new EventEmitter();

  showQuestionId: boolean;
  isSuperAdmin: boolean;
  isLocked: boolean = true;
  tabIndex: number = 0;
  originalDisabledValue: boolean;
  lockOnlyDisabledValue: boolean;

  constructor(
    private baseUrl: BaseURLService,
    private config: ConfigStateService,
    private taxReturnService: TaxReturnService,
    private userInfo: UserInfoService
  ) {}

  ngOnInit() {
    this.showQuestionId = this.config.tenant !== Tenant.AU;
    this.isSuperAdmin = this.userInfo.isSuperAdmin;
    this.originalDisabledValue = this.disabled;
    // we only need to worry about controlling locks from this level for scalar questions
    // for vectors it is controlled by the table
    this.isLocked = !this.question?.parentId;
    // 0 = able to tab through as normal, -1 = tab is prevented for this element
    this.updateLockAccessConditions();
  }

  showOverRideQuestion(q: IFormField) {
    return !this.hideClearOverRideQuestionIcon &&
      q &&
      q.answers &&
      q.answers.length &&
      q.answers[0].overrideStatus === 1 &&
      !(q.displayFieldType === "file" && q.sourceId)
      ? true
      : false;
  }

  overRideAnswer(question: IFormField) {
    if (
      window.confirm(
        "Are you sure you want to clear overrides for this question?"
      )
    ) {
      const answerIds = [question.answers[0].id];
      const taxReturnId =
        question && question.answers && question.answers[0]
          ? question.answers[0].taxReturnId
          : 0;
      this.taxReturnService
        .clearOverridesForTaxReturn(taxReturnId, {
          answerIds,
          updateAnswerFlag: true
        })
        .subscribe(result => {
          this.refreshTaxReturn.emit(true);
        });
    }
  }

  getControl(q) {
    // get from control in a specific row
    if (this.row != null) {
      return this.formGroup.get(`${q.id}-${this.row}`);
    } else {
      return this.formGroup.get(String(q.id));
    }
  }

  nonEmpty(q) {
    // return false;
    if (this.row != null) {
      const value = this.formGroup.get(`${q.id}-${this.row}`).value;
      return (
        q.answers &&
        q.answers.length &&
        (q.answers[0].overrideStatus ||
          (!q.answers[0].overrideStatus && q.answers[0].answer !== value))
      );
    } else {
      const value = this.formGroup.get(String(q.id)).value;
      return (
        q.answers &&
        q.answers.length &&
        (q.answers[0].overrideStatus ||
          (!q.answers[0].overrideStatus && q.answers[0].answer !== value))
      );
    }
  }

  onBlur() {
    this.blur.emit();
  }

  goToSection(type: any, id: any) {
    const newTab = window.open(`${this.baseUrl.get()}/settings/${type}/${id}`);
    newTab.blur();
    window.focus();
  }

  toggleLock() {
    this.isLocked = !this.isLocked;
    this.updateLockAccessConditions();
  }

  updateLockAccessConditions() {
    // tabIndex = 0, tab behaviour is as normal; tabIndex = -1, element is not accessible via tabbing
    this.tabIndex =
      this.question.lockType === "manualInput" || !this.isLocked ? 0 : -1;
    if (
      (this.question?.displayFieldType === "file" ||
        this.question?.displayFieldType === "image") &&
      this.tabIndex === -1
    ) {
      this.disabled = true;
      this.lockOnlyDisabledValue = true;
    } else {
      this.disabled = this.originalDisabledValue;
      this.lockOnlyDisabledValue = false;
    }
  }
}

export const makeControlForQuestion = (
  q: IFormField,
  isTableCell?: boolean,
  answer?: IAnswer
): UntypedFormControl => {
  const fieldType = q.displayFieldType.toLocaleLowerCase();
  const dataType = q.dataType.toLocaleLowerCase();

  const ans = isTableCell
    ? answer
    : q.answers && q.answers.length > 0
    ? q.answers[0]
    : null;

  q.questionDropdowns = q.questionDropdowns ? q.questionDropdowns.sort((a, b) => a.order - b.order) : []; // order dropdown values by selected order

  if (fieldType === "text" || fieldType === "textarea") {
    if (dataType === "string") {
      return makeTextFieldFormControl(q, ans);
    } else if (dataType === "integer" || dataType === "float") {
      return makeNumberFieldFormControl(q, ans);
    }
  } else if (fieldType === "checkbox") {
    return makeCheckboxFieldFormControl(q, ans);
  } else if (fieldType === "radio") {
    return makeRadioFieldFormControl(q, ans);
  } else if (fieldType === "date picker") {
    return makeDateFieldFormControl(q, ans);
  } else if (fieldType === "drop down") {
    return makeDropdownFieldFormControl(q, ans);
  } else if (fieldType === "people") {
    return makeDropdownFieldFormControl(q, ans);
  } else if (fieldType === "image") {
    return makeImageFieldFormControl(q, ans);
  } else if (fieldType === "file") {
    return makeFileFieldFormControl(q, ans);
  }
};

export const setupDependantMandatory = (
  group: IGroup,
  groupQuestions: Array<IFormField>,
  formGroup: UntypedFormGroup,
  allQuestionsById,
  dependantMandatoryChanged?: Subject<void>
) => {
  const subscriptions = [];

  const parentValueIsDependantValue = (parentValue, dependantValue) => {
    return (
      parentValue !== "" &&
      (dependantValue == null ||
        dependantValue === "" ||
        parentValue === dependantValue)
    );
  };

  groupQuestions
    .filter(q => q.dependantMandatoryParentId != null)
    .forEach(q => {
      // get the dependant question
      const parentQuestion = head(
        groupQuestions.filter(qq => qq.id === q.dependantMandatoryParentId)
      );
      if (!parentQuestion) {
        // Parent question is not in the group, assume that then the parent question is not a tab question
        // and check the first answer in it.
        const fullQuestion = allQuestionsById[q.dependantMandatoryParentId];
        const answer = fullQuestion ? head(fullQuestion.answers) : undefined;
        if (answer) {
          // @ts-ignore TODO fix types
          const parentValue = answer.answer;
          if (
            parentValueIsDependantValue(
              parentValue,
              q.dependantMandatoryParentValue
            )
          ) {
            const formControl = formGroup.get(String(q.id));
            formControl.setValidators(
              Validators.compose(q.validators.concat([Validators.required]))
            );
          }
        }
      } else {
        const parentFormControl: AbstractControl = formGroup.get(
          String(parentQuestion.id)
        );
        if (parentFormControl) {
          const handleParentValueChange = newVal => {
            // current field's form control
            const formControl: AbstractControl = formGroup.get(String(q.id));
            if (
              parentValueIsDependantValue(
                newVal,
                q.dependantMandatoryParentValue
              )
            ) {
              formControl.setValidators(
                Validators.compose(q.validators.concat([Validators.required]))
              );
            } else {
              formControl.setValidators(Validators.compose(q.validators));
            }
            formControl.updateValueAndValidity();
            if (dependantMandatoryChanged) {
              dependantMandatoryChanged.next();
            }
          };

          // watch the parent answer changing, and update the validators accordingly
          subscriptions.push(
            parentFormControl.valueChanges.subscribe(newVal =>
              handleParentValueChange(newVal)
            )
          );
          // initialize for the existing value (as the sub only fires when it changes later)
          handleParentValueChange(parentFormControl.value);
        }
      }
    });
  return subscriptions;
};
